import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getOrderInProgress,
  hasCardPaymentInProgress,
  hasOrderOpenPriceItem,
  tabbedOrderHasPriceOverrideItem,
} from '../../selectors/order'
import { isAuthFailedTab, isTabbed, STADIUM_ORDER_STATES } from '../../utils/orderStates'
import {
  getCashEnabled,
  getGiftCardEnabled,
  getMobilePaySetting,
  getTicketEnabled,
  makeGetNoOpTenders,
} from '../../selectors/revenueCenter'
import { PAYMENT_TYPES, PAYMENT_TYPES_INTERNAL } from '../../utils/paymentTypes'
import { getAndroidSDKVersion } from '../../VNAndroidSDK/Selectors'
import { getTabsModeEnabled } from '../../selectors/menus'
import { isSDKCompatible } from '../../VNAndroidSDK/Utils'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { MOBILE_PAY_SCAN_QR, MOBILE_PAY_SHOW_QR, VERSION_MIN_FOR_SPLIT } from '../Utils'
import { getPpiConfigurations } from '../../VNPPI/Selectors'
import { getTender } from '../../VNPPI/Utils'
import { VNPPI_TENDERS } from '../../VNPPI/Enums'
import { cartHasPriceOverrideItem, hasOpenPriceItem } from '../../selectors/cart'
import { getReopenedTabPayments } from '../Selectors'
import { addReopenPaymentToOrderInProgress } from '../../actions/order'
import { APPLICATION_STRINGS } from '../../strings'
import { getIsOrderTaxExempt } from '../../VNTaxes/Selectors'

export const SCAN_TENDERS = [PAYMENT_TYPES.QR_PAY, PAYMENT_TYPES.GIFT_CARD, PAYMENT_TYPES.TICKET]

// if there is a max amount custom tender and that tender is in the order in progress, dont show it anymore.
// since we already used it.
export const isTenderUsed = (tender, fromSplit, order) => {
  if (tender?.maximumAmountInCents) return false

  if (tender?.maximumAmountInCents && fromSplit && order?.payments?.some((payment) => payment.paymentType === tender.name)) {
    return true
  }

  return false
}

export const useTenders = () => {
  const order = useSelector(getOrderInProgress)
  const cfdMode = useSelector(getDeviceMode)
  const androidSDKVersion = useSelector(getAndroidSDKVersion)
  const hasCardAuth = useSelector(hasCardPaymentInProgress)
  const cashEnabled = useSelector(getCashEnabled)
  const giftCardEnabled = useSelector(getGiftCardEnabled)
  const mobilePaySetting = useSelector(getMobilePaySetting)
  const noOpTenders = useSelector(makeGetNoOpTenders())
  const tabsModeEnabled = useSelector(getTabsModeEnabled)
  const ticketEnabled = useSelector(getTicketEnabled)
  const isTabOrder = isTabbed(order)
  const isTabAuthFailed = isAuthFailedTab(order)
  const ppiConfigurations = useSelector((state) => getPpiConfigurations(state))
  const reopenedTabPayments = useSelector((state) => getReopenedTabPayments(state, order?.uuid))
  const dispatch = useDispatch()
  const cartHasOpenItem = useSelector(hasOpenPriceItem)
  const orderHasOpenPrice = useSelector(hasOrderOpenPriceItem)
  const cartHasPriceOverride = useSelector(cartHasPriceOverrideItem)
  const orderHasPriceOverride = useSelector(tabbedOrderHasPriceOverrideItem)
  const isOrderTaxExempt = useSelector(getIsOrderTaxExempt)

  /**
   * Block rich checkout if there is an open price item in the cart
   * This is not a currently supported flow for tabs
   */
  const blockRichCheckoutOrderText = useMemo(() => {
    if (order?.isTab) {
      if (orderHasPriceOverride) {
        return APPLICATION_STRINGS.PRICE_OVERRIDE_RICH_CHECKOUT_CLOSING_ORDER
      }

      if (orderHasOpenPrice) {
        return APPLICATION_STRINGS.OPEN_PRICE_RICH_CHECKOUT_CLOSING_TABS_DIALOG
      }

      if (isOrderTaxExempt) {
        return APPLICATION_STRINGS.TAX_EXEMPT_RICH_CHECKOUT_CLOSING_TABS_DIALOG
      }

      return null
    }

    if (cartHasPriceOverride) {
      return APPLICATION_STRINGS.PRICE_OVERRIDE_RICH_CHECKOUT_CLOSING_ORDER
    }

    if (cartHasOpenItem) {
      return APPLICATION_STRINGS.OPEN_PRICE_RICH_CHECKOUT_CLOSING_TABS_DIALOG
    }

    if (isOrderTaxExempt) {
      return APPLICATION_STRINGS.TAX_EXEMPT_RICH_CHECKOUT_CLOSING_TABS_DIALOG
    }

    return null
  }, [cartHasOpenItem, cartHasPriceOverride, order?.isTab, orderHasOpenPrice, orderHasPriceOverride, isOrderTaxExempt])

  /**
   * Formats currently selected payment
   */
  const getSavedPaymentAsTender = useCallback(() => {
    const prefix = order.paymentDisplay || order?.cardReaderData?.cardIssuer
    let suffix = ''

    if (order?.cardReaderData?.cardholderName) {
      suffix = `- ${order.cardReaderData.cardholderName}`
    }

    return [PAYMENT_TYPES_INTERNAL.SAVED_CARD, `${prefix} ${suffix}`, false, null]
  }, [order])

  const isSplitEnabled = useCallback(
    (fromSplit) => {
      if (fromSplit) return false
      if (!tabsModeEnabled) return false

      return cfdMode !== MODES.POS || isSDKCompatible(androidSDKVersion, VERSION_MIN_FOR_SPLIT)
    },
    [androidSDKVersion, cfdMode, tabsModeEnabled],
  )

  const addNoOpTenders = useCallback(
    (paymentTypes, fromSplit, isBackBar) => {
      noOpTenders?.forEach((tender) => {
        // if fixed custom tender is enabled and device is in back bar mode
        // don't show fixed custom tender
        if (isBackBar && tender?.maximumAmountInCents) return

        if (isTenderUsed(tender, fromSplit, order)) return

        paymentTypes.push([tender.name, tender.displayName, tender.managerApproval, tender.maximumAmountInCents])
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noOpTenders],
  )

  const addCashTender = useCallback(
    (paymentTypes) => {
      if (cashEnabled) {
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.CASH, 'Cash', false, null])
      }
    },
    [cashEnabled],
  )

  const getReopenPayments = useCallback(() => {
    return reopenedTabPayments.map((payment) => {
      let description = payment.shortDescription

      if (payment.userAttributes?.cardholderName) {
        description = `${description} • ${payment.userAttributes.cardholderName}`
      }

      return [PAYMENT_TYPES_INTERNAL.REOPEN_SAVED_CARD, description, false, null, payment.paymentId]
    })
  }, [reopenedTabPayments])

  const addReopenPayment = useCallback(
    (paymentId) => {
      const payment = reopenedTabPayments.get(paymentId)
      dispatch(addReopenPaymentToOrderInProgress({ orderUuid: order?.uuid, payment }))
    },
    [reopenedTabPayments, dispatch, order?.uuid],
  )

  /**
   * Tender Selection for Pay Now, Tabs, Splits and non-BackBar Use Cases
   */
  const getAvailableTenders = useCallback(
    (fromSplit = false, previousPaymentType = null) => {
      const ppiTicketTender = getTender(ppiConfigurations, VNPPI_TENDERS.TICKET_TENDER)
      const ppiQRPayTender = getTender(ppiConfigurations, VNPPI_TENDERS.QRPAY_TENDER)
      const ppiGiftcardTender = getTender(ppiConfigurations, VNPPI_TENDERS.GIFTCARD_TENDER)
      const isTicketScreen = previousPaymentType === PAYMENT_TYPES.TICKET

      const isNewOrder = !Object.keys(order)?.length
      const isUnpaidSubmitted = order?.state === STADIUM_ORDER_STATES.SUBMITTED && !order?.payments?.[0]?.amountInCents
      const showAllPayments = isNewOrder || isTabOrder || isTabAuthFailed || isUnpaidSubmitted
      let paymentTypes = []

      // Split Payment Option at the top
      if (isSplitEnabled(fromSplit)) {
        paymentTypes.push(['split', 'Split', false, null])
      }

      //push reopen payments to the payments list also add support auth for scanned tenders with stashed payments
      if (order.reopened || (isTabbed(order) && fromSplit)) {
        getReopenPayments().forEach((payment) => {
          const paymentAlreadyApplied = order.payments?.find((pay) => {
            return pay.paymentId === payment[4]
          })

          if (!paymentAlreadyApplied) {
            paymentTypes.push(payment)
          }
        })
      }

      // Saved Card - Auth'ed Card cannot be use in split
      if (hasCardAuth && isTabbed(order) && !fromSplit) {
        paymentTypes.push(getSavedPaymentAsTender())
      }

      if (!tabsModeEnabled) addNoOpTenders(paymentTypes, fromSplit)

      // card is always an option
      paymentTypes.push([PAYMENT_TYPES_INTERNAL.CREDIT_CARD, 'Card', false, null])

      if (!tabsModeEnabled) addCashTender(paymentTypes)

      if (fromSplit) {
        if (tabsModeEnabled) {
          addNoOpTenders(paymentTypes, fromSplit)
          addCashTender(paymentTypes)
        }
        return paymentTypes
      }

      if (!showAllPayments) {
        // Enable Ticket on ticket screen
        if (isTicketScreen && ticketEnabled) {
          const buttonName = ppiTicketTender?.name ? ppiTicketTender?.name : 'Ticket'
          paymentTypes.push([PAYMENT_TYPES_INTERNAL.TICKET, buttonName, false, null])
        }
        return paymentTypes
      }

      if (mobilePaySetting === MOBILE_PAY_SHOW_QR) {
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.VIRTUAL_CURRENCY, 'App', false, null])
      }

      if (mobilePaySetting === MOBILE_PAY_SCAN_QR) {
        const buttonName = ppiQRPayTender?.name ? ppiQRPayTender?.name : 'QR Pay'
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.QR_PAY, buttonName, false, null])
      }

      // ticket enabled for showAllPayments
      if (ticketEnabled) {
        const buttonName = ppiTicketTender?.name ? ppiTicketTender?.name : 'Ticket'
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.TICKET, buttonName, false, null])
      }

      if (giftCardEnabled) {
        const buttonName = ppiGiftcardTender?.name ? ppiGiftcardTender?.name : 'Gift Card'
        paymentTypes.push([PAYMENT_TYPES_INTERNAL.GIFT_CARD, buttonName, false, null])
      }

      if (tabsModeEnabled) {
        addNoOpTenders(paymentTypes, fromSplit)
        addCashTender(paymentTypes)
      }

      return paymentTypes
    },
    [
      addCashTender,
      addNoOpTenders,
      getSavedPaymentAsTender,
      giftCardEnabled,
      hasCardAuth,
      isSplitEnabled,
      isTabAuthFailed,
      isTabOrder,
      mobilePaySetting,
      order,
      tabsModeEnabled,
      ticketEnabled,
      ppiConfigurations,
      getReopenPayments,
    ],
  )

  /**
   * Tender Selection as Required for BackBar
   * Options include: Auth'ed (saved) card, custom tenders, card and cash
   */
  const getBackBarTenders = useCallback(() => {
    let paymentTypes = []

    // currently pre-authorized Card
    if (hasCardAuth && isTabbed(order)) {
      paymentTypes.push(getSavedPaymentAsTender())
    }

    //push reopen payments to the payments list
    if (order.reopened) {
      getReopenPayments().forEach((payment) => {
        const paymentAlreadyApplied = order.payments.find((pay) => {
          return pay.paymentId === payment[4]
        })

        if (!paymentAlreadyApplied) {
          paymentTypes.push(payment)
        }
      })
    }

    if (!tabsModeEnabled) addNoOpTenders(paymentTypes, false, true)

    // card is always an option
    paymentTypes.push([PAYMENT_TYPES_INTERNAL.CREDIT_CARD, 'Card', false, null])

    if (tabsModeEnabled) addNoOpTenders(paymentTypes, false, true)
    addCashTender(paymentTypes)

    return paymentTypes
  }, [addCashTender, addNoOpTenders, getSavedPaymentAsTender, hasCardAuth, order, tabsModeEnabled, getReopenPayments])

  const orderHasCashPayment = useMemo(() => {
    return order?.payments?.some((payment) => payment.paymentType === PAYMENT_TYPES_INTERNAL.CASH) || false
  }, [order])

  return {
    blockRichCheckoutOrderText,
    getAvailableTenders,
    getBackBarTenders,
    addReopenPayment,
    orderHasCashPayment,
  }
}

export default useTenders
